// (C) Copyright 2020 CNCC Services <https://www.cncc.fr/>
// This program is the sole property of CNCC Services and is not free software.

import { Link, RouteComponentProps, Router } from "@reach/router";
import { Layout, Spin } from "antd";
import { useStore } from "effector-react";
import React, { FunctionComponent, useEffect } from "react";
import { useTranslation } from "react-i18next";
import "./App.css";
import { AuthentifiedUser } from "./api/backend-openapi";
import {
  refreshAuthCookie,
  requestGetUserInfo
} from "./state/auth/AuthEffects";
import { authentifiedUserEvent } from "./state/auth/AuthEvents";
import { authentifiedUserStore } from "./state/auth/AuthStore";
import { requestGetBranches } from "./state/branches/BranchesEffects";
import { setBranchList } from "./state/branches/BranchesEvents";
import { requestGetProfiles } from "./state/profiles/ProfilesEffects";
import { setProfileList } from "./state/profiles/ProfilesEvents";
import Breadcrumbs from "./ui/components/Breadcrumbs";
import SideMenu from "./ui/components/SideMenu";
import { SpinIcon } from "./ui/components/SpinIcon";
import AuthError from "./ui/views/authentication/AuthError";
import ForbiddenView from "./ui/views/authentication/Forbidden";
import ForcedLogOutView from "./ui/views/authentication/ForcedLogOut";
import LoginCallback from "./ui/views/authentication/LoginCallback";
import LogoutCallback from "./ui/views/authentication/LogoutCallback";
import AddBroadcastListScreen from "./ui/views/broadcastList/add/AddBroadcastListScreen";
import BroadcastListScreen from "./ui/views/broadcastList/details/BroadcastListScreen";
import BroadcastListsScreen from "./ui/views/broadcastList/list/BroadcastListsScreen";
import DashboardScreen from "./ui/views/dashboard/DashboardScreen";
import KeywordsScreen from "./ui/views/keyword/list/KeywordsScreen";
import AddNewsScreen from "./ui/views/news/add/AddNewsScreen";
import NewsScreen from "./ui/views/news/details/NewsScreen";
import NewsListScreen from "./ui/views/news/list/NewsListScreen";
import AddNewsThemeScreen from "./ui/views/newsTheme/add/AddNewsThemeScreen";
import NewsThemeScreen from "./ui/views/newsTheme/details/NewsThemeScreen";
import NewsThemesScreen from "./ui/views/newsTheme/list/NewsThemesScreen";
import ProfileListScreen from "./ui/views/profiles/list/ProfileListScreen";
import SettingsScreen from "./ui/views/settings/SettingsScreen";
import StatisticsScreen from "./ui/views/statistics/StatisticsScreen";
import AddSurveyScreen from "./ui/views/surveys/add/AddSurveyScreen";
import SurveyScreen from "./ui/views/surveys/details/SurveyScreen";
import SurveysListScreen from "./ui/views/surveys/list/SurveysListScreen";
import ThematicDossiersScreen from "./ui/views/thematicDossier/list/ThematicDossierScreen";
import AddUserScreen from "./ui/views/userManagement/add/AddUserScreen";
import UserScreen from "./ui/views/userManagement/details/UserScreen";
import UsersListScreen from "./ui/views/userManagement/list/UsersListScreen";
import configuration from "./utils/Config";
import { authRedirectKey } from "./utils/Constant";
import "./utils/i18n/i18n";
import logger from "./utils/logger";
import AddThematicDossierScreen from "./ui/views/thematicDossier/add/AddThematicDossierScreen";
import ThematicDossierScreen from "./ui/views/thematicDossier/details/ThematicDossierScreen";
import { requestGetKeywords } from "./state/keyword/KeywordEffects";
import { setKeywords } from "./state/keyword/KeywordEvents";
import MissingUserListsScreen from "./ui/views/missingUser/list/MissingUserListsScreen";

const { Sider, Content } = Layout;

export const Application: FunctionComponent<RouteComponentProps> = () => {
  const { t } = useTranslation();
  const authentifiedUser = useStore(authentifiedUserStore);

  useEffect(() => {
    return requestGetBranches.done.watch(({ result }) => {
      if (result.ok && result.data) {
        setBranchList(result.data);
      }
    });
  });

  useEffect(() => {
    requestGetBranches({});
  }, []);

  useEffect(() => {
    return requestGetKeywords.done.watch(({ result }) => {
      if (result.ok && result.data) {
        setKeywords(result.data);
      }
    });
  });

  useEffect(() => {
    requestGetKeywords();
  }, []);

  useEffect(() => {
    return requestGetProfiles.done.watch(({ result }) => {
      if (result.ok && result.data) {
        setProfileList(result.data);
      }
    });
  });

  useEffect(() => {
    requestGetProfiles({});
  }, []);

  return (
    <Layout style={{ height: "100vh" }}>
      <header
        className={
          "d-flex flex-nowrap align-items-center justify-content-between px-5 py-3"
        }
      >
        <Link to="/">
          <img
            alt={t("global.alt.logo")}
            className="logo px-2"
            src="/static/img/logo.jpeg"
          />
        </Link>
        <div className="user d-flex align-items-center">
          {authentifiedUser && (
            <>
              <div className="d-flex flex-column align-items-end justify-content-center px-2">
                <div className="user-name">
                  {authentifiedUser?.firstName} {authentifiedUser?.lastName}{" "}
                  {authentifiedUser?.sub && authentifiedUser?.email
                    ? `(${authentifiedUser.sub} / ${authentifiedUser.email})`
                    : ""}
                </div>
                <Link
                  to="#"
                  className="user-logout"
                  onClick={() => {
                    logger.debug("Log out");
                    window.location.href = `${configuration.backendBaseUrl}/auth/logout?post_logout_redirect_uri=${configuration.postLogoutUrl}`;
                  }}
                >
                  {t("global.logout")}
                </Link>
              </div>
            </>
          )}
          {/* <div className="user-avatar">
            <Avatar src={context.user?.profile.picture || undefined} />
          </div> */}
        </div>
      </header>
      <Layout>
        <Sider breakpoint="lg" collapsedWidth="0" width="225px">
          <SideMenu />
        </Sider>
        <Layout className="content-container position-relative">
          <Breadcrumbs />
          <Content className="content">
            <Router>
              <DashboardScreen path="/" />
              <NewsListScreen path="/news/">
                <AddNewsScreen path="addNews" />
                <NewsScreen path=":newsId" />
              </NewsListScreen>
              <AddNewsScreen path="/news/addNews" />
              <NewsScreen path="/news/:newsId" />
              <NewsThemesScreen path="/newsThemes" />
              <AddNewsThemeScreen path="/newsThemes/add" />
              <NewsThemeScreen path="/newsThemes/:newsThemeId" />
              <KeywordsScreen path="/keywords" />
              <ThematicDossiersScreen path="/thematicDossiers" />
              <AddThematicDossierScreen path="/thematicDossiers/add" />
              <ThematicDossierScreen path="/thematicDossiers/:thematicDossierId" />
              <SettingsScreen path="/settings" />
              {((authentifiedUser?.branch &&
                authentifiedUser.branch.includes("crcc")) ||
                !authentifiedUser?.branch) && (
                <StatisticsScreen path="/statistics" />
              )}
              <SurveysListScreen path="/surveys" />
              <AddSurveyScreen path="/surveys/add" />
              <SurveyScreen path="/surveys/:surveyId" />
              <UsersListScreen path="/users" />
              <ProfileListScreen path="/profiles" />
              <AddUserScreen path="/users/add" />
              <UserScreen path="/users/:userId" />
              <BroadcastListsScreen path="/broadcastLists" />
              <AddBroadcastListScreen path="/broadcastLists/add" />
              <BroadcastListScreen path="/broadcastLists/:broadcastListId" />
              <MissingUserListsScreen path="/missingUsers" />
            </Router>
          </Content>
        </Layout>
      </Layout>
    </Layout>
  );
};

const AppWithAuth: React.FC<RouteComponentProps> = () => {
  useEffect(() => {
    requestGetUserInfo.done.watch(({ result }) => {
      if (!!result.data) {
        logger.debug("User info success ", result.data);
        sessionStorage.removeItem(authRedirectKey);
        authentifiedUserEvent(result.data);
      } else {
        logger.debug("User info unavailable => login");
        const authRedirectUrl =
          window.location.pathname === "/logout_callback"
            ? "/"
            : window.location.pathname;
        logger.debug("Target URL after login", authRedirectUrl);
        sessionStorage.setItem(authRedirectKey, authRedirectUrl);
        window.location.href = `${configuration.backendBaseUrl}/auth/login?redirect_uri=${configuration.postLoginUrl}`;
      }
    });
  }, []);
  useEffect(() => {
    const interval = setInterval(() => {
      refreshAuthCookie({});
    }, 300000);
    return () => clearInterval(interval);
  }, []);
  const authentifiedUser: AuthentifiedUser | null = useStore(
    authentifiedUserStore
  );
  if (!authentifiedUser) {
    return (
      <div style={{ textAlign: "center" }}>
        <Spin indicator={SpinIcon} />
      </div>
    );
  }
  sessionStorage.removeItem(authRedirectKey);
  return <Application />;
};

const AppWithCallBack: React.FC = () => {
  useEffect(() => {
    requestGetUserInfo({});
  });
  return (
    <Router>
      <LoginCallback path="/login_callback" />
      <LogoutCallback path="/logout_callback" />
      <AuthError path="/authError" />
      <ForbiddenView path="/forbidden" />
      <ForcedLogOutView path="/forcedLogOut" />
      <AppWithAuth default />
    </Router>
  );
};

export default AppWithCallBack;
